/* eslint-disable custom/no-hardcoded-colors */
import { withOnClickTracking } from 'auto-tracking';
import { ButtonGroup, rem, SkeletonLoader, styled, theme } from 'design-system';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { UploadButtonNew } from 'preflight-core';
import { useEffect, useRef, useState } from 'react';

import { useEventTracking } from '../../../contexts/tracking-context';
import { Check, Journal } from '../../../resources/journal';
import { mutateString } from '../../../utils/replace-language';
import { Button } from '../../button';
import { ButtonNew } from '../../button-aipp';
import { mapJournalToVariant } from '../../journal-variant';
import { CustomFontHeaderContainer } from '../../section';
import { ChangeArticleConfig } from './change-article-config';

export const Section1HomepageLHS = ({
  journal,
  loading,
  languageCheckLists,
  technicalCheckLists,
  setSelectedArticleTypeId,
  onFileSelected,
  uploadState,
  showQuickView,
  setErrorScreen,
  errorSection,
  setErrorSection,
  setOtherSectionClicked,
  techCheckCount,
  langCheckCount,
  showLogo,
  submitPage,
  showFullJournalName = false,
}: {
  journal: Journal;
  loading: boolean;
  languageCheckLists: Check[];
  technicalCheckLists: Check[];
  setSelectedArticleTypeId: (selectedId: number) => void;
  onFileSelected: (file: File) => void;
  uploadState: { name: string };
  showQuickView: boolean;
  setErrorScreen: (value: boolean) => void;
  errorSection: string;
  setErrorSection: (value: string) => void;
  setOtherSectionClicked: (value: boolean) => void;
  techCheckCount: number;
  langCheckCount: number;
  showLogo: boolean;
  submitPage?: boolean;
  showFullJournalName?: boolean;
}) => {
  const t = useTranslation('homepage').t;
  const [showTooltip, setShowTooltip] = useState(false);
  const { trackEvent } = useEventTracking();
  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };
  const mappedJournal = mapJournalToVariant(journal.publisher.slug);
  const [showTooltip2, setShowTooltip2] = useState(false);
  const [showTooltip3, setShowTooltip3] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const handleMouseEnter2 = () => {
    setShowTooltip2(true);
  };
  const handleMouseLeave2 = () => {
    setShowTooltip2(false);
  };
  const handleMouseEnter3 = () => {
    setShowTooltip3(true);
  };
  const handleMouseLeave3 = () => {
    setTimeout(() => {
      if (!tooltipRef.current?.matches(':hover')) {
        setShowTooltip3(false);
      }
    }, 100);
  };
  const handleTooltipMouseEnter = () => {
    setShowTooltip3(true);
  };

  const handleTooltipMouseLeave = () => {
    setShowTooltip3(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      tooltipRef.current &&
      !tooltipRef.current.contains(event.target as Node)
    ) {
      setShowTooltip3(false);
    }
  };
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return (
    <>
      <MainSection
        style={{
          paddingTop:
            journal.publisher.slug === 'gjd-journal-finder' ||
            journal.publisher.slug === 'editage-cn'
              ? '50px'
              : '50px',
          paddingBottom: '40px',
        }}
      >
        {showLogo ? (
          //Section 1 for IEEE
          journal.publisher.slug === 'ieee' ? (
            <>
              <CustomFontHeaderContainer journal={mappedJournal}>
                <MainHeading
                  style={{
                    fontSize: '24px',
                    lineHeight: '32px',
                    fontWeight: 600,
                  }}
                >
                  {t('section1.ready_to_submit_to', 'Ready to submit to')}
                </MainHeading>
              </CustomFontHeaderContainer>
              <SubHeadingNew
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                  fontSize: '28px',
                }}
                aria-label={`${journal.publisher.label} / ${journal.label}`}
                data-test-id="journal-heading"
              >
                {truncateLabel(journal.label ?? '', 20)}
                {showTooltip ? (
                  <Tooltip>
                    {' '}
                    {journal.publisher.label} / {journal.label}
                  </Tooltip>
                ) : (
                  <></>
                )}
                {journal.articleTypes?.length === 1 ? (
                  <></>
                ) : (
                  <ChangeArticleConfig
                    journal={journal}
                    onOptionSelect={(selectedId: string) =>
                      setSelectedArticleTypeId(Number(selectedId))
                    }
                  />
                )}
              </SubHeadingNew>
              <SubHeadingNew2>
                {t(
                  'v2_new_content.assessment_check_journal_requirements',
                  'Assessment check based on journal requirements ',
                )}
              </SubHeadingNew2>

              {loading ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <SkeletonLoader
                    height="27px"
                    width="35%"
                    background={theme.colors.gray5.value}
                  />
                  <SkeletonLoader
                    height="27px"
                    width="45%"
                    background={theme.colors.gray5.value}
                  />
                </div>
              ) : (
                <TextDefault2>
                  {techCheckCount > 0 && langCheckCount > 0 ? (
                    <>
                      <LangTechEvaluations>
                        <Image
                          width={16}
                          height={22}
                          src="/green-new-tick.svg"
                          alt="Green Tick"
                          style={{
                            display: 'inline',
                            marginRight: '8px',

                            verticalAlign: 'middle',
                          }}
                        />
                        <span
                          data-test-id="language-check-count"
                          style={{
                            fontWeight: 'bold',
                            paddingRight: '4px',
                            cursor: 'pointer',
                            color: showTooltip2
                              ? theme.colors.blue10.value
                              : 'inherit',
                          }}
                          onMouseEnter={() => handleMouseEnter2()}
                          onMouseLeave={() => handleMouseLeave2()}
                        >
                          {langCheckCount}
                        </span>
                        {mutateString(
                          t('section1_v1.is_manuscript_ready_description.lang'),
                          {},
                        )}
                      </LangTechEvaluations>
                      {showTooltip2 ? (
                        <TooltipNew>
                          <ExpandableTextUL>
                            {languageCheckLists.map((check, index) => (
                              <ExpandableTextLI key={index}>
                                <span>{check.label}</span>
                              </ExpandableTextLI>
                            ))}
                          </ExpandableTextUL>
                        </TooltipNew>
                      ) : (
                        <></>
                      )}

                      <LangTechEvaluations>
                        <Image
                          width={16}
                          height={22}
                          src="/green-new-tick.svg"
                          alt="Green Tick"
                          style={{
                            display: 'inline',
                            marginRight: '8px',
                            verticalAlign: 'middle',
                          }}
                        />
                        <span
                          data-test-id="technical-check-count"
                          style={{
                            fontWeight: 'bold',
                            paddingRight: '4px',
                            cursor: 'pointer',
                            color: showTooltip3
                              ? theme.colors.blue10.value
                              : 'inherit',
                          }}
                          onMouseEnter={() => handleMouseEnter3()}
                          onMouseLeave={() => handleMouseLeave3()}
                        >
                          {techCheckCount}
                        </span>
                        {mutateString(
                          t('section1_v1.is_manuscript_ready_description.tech'),
                          {},
                        )}
                      </LangTechEvaluations>
                      {showTooltip3 ? (
                        <TooltipNew2
                          ref={tooltipRef}
                          onMouseEnter={handleTooltipMouseEnter}
                          onMouseLeave={handleTooltipMouseLeave}
                        >
                          <ExpandableTextUL>
                            {technicalCheckLists.map((check, index) => (
                              <ExpandableTextLI key={index}>
                                <span>{check.label}</span>
                              </ExpandableTextLI>
                            ))}
                          </ExpandableTextUL>
                        </TooltipNew2>
                      ) : (
                        <></>
                      )}
                    </>
                  ) : techCheckCount > 0 ? (
                    <>
                      <LangTechEvaluations>
                        <Image
                          width={16}
                          height={22}
                          src="/green-new-tick.svg"
                          alt="Green Tick"
                          style={{
                            display: 'inline',
                            marginRight: '8px',
                            verticalAlign: 'middle',
                          }}
                        />
                        <span
                          data-test-id="technical-check-count"
                          style={{
                            fontWeight: 'bold',
                            paddingRight: '4px',
                            cursor: 'pointer',
                            color: showTooltip3
                              ? theme.colors.blue10.value
                              : 'inherit',
                          }}
                          onMouseEnter={() => handleMouseEnter3()}
                          onMouseLeave={() => handleMouseLeave3()}
                        >
                          {techCheckCount}
                        </span>
                        {mutateString(
                          t('section1_v1.is_manuscript_ready_description.tech'),
                          {},
                        )}
                      </LangTechEvaluations>
                      {showTooltip3 ? (
                        <TooltipNew2
                          ref={tooltipRef}
                          onMouseEnter={handleTooltipMouseEnter}
                          onMouseLeave={handleTooltipMouseLeave}
                        >
                          <ExpandableTextUL>
                            {technicalCheckLists.map((check, index) => (
                              <ExpandableTextLI key={index}>
                                <span>{check.label}</span>
                              </ExpandableTextLI>
                            ))}
                          </ExpandableTextUL>
                        </TooltipNew2>
                      ) : (
                        <></>
                      )}
                    </>
                  ) : langCheckCount > 0 ? (
                    <>
                      <LangTechEvaluations>
                        <Image
                          width={16}
                          height={22}
                          src="/green-new-tick.svg"
                          alt="Green Tick"
                          style={{
                            display: 'inline',
                            marginRight: '8px',
                            verticalAlign: 'middle',
                          }}
                        />
                        <span
                          data-test-id="language-check-count"
                          style={{
                            fontWeight: 'bold',
                            paddingRight: '4px',
                            cursor: 'pointer',
                            color: showTooltip2
                              ? theme.colors.blue10.value
                              : 'inherit',
                          }}
                          onMouseEnter={() => handleMouseEnter2()}
                          onMouseLeave={() => handleMouseLeave2()}
                        >
                          {langCheckCount}
                        </span>
                        {mutateString(
                          t('section1_v1.is_manuscript_ready_description.lang'),
                          {},
                        )}
                      </LangTechEvaluations>
                      {showTooltip2 ? (
                        <TooltipNew>
                          <ExpandableTextUL>
                            {languageCheckLists.map((check, index) => (
                              <ExpandableTextLI key={index}>
                                <span>{check.label}</span>
                              </ExpandableTextLI>
                            ))}
                          </ExpandableTextUL>
                        </TooltipNew>
                      ) : (
                        <></>
                      )}
                    </>
                  ) : null}
                </TextDefault2>
              )}
            </>
          ) : (
            //Section 2 for other publishers except paperpal, gjd-journal-finder , editage-us
            <>
              <CustomFontHeaderContainer journal={mappedJournal}>
                <MainHeading
                  style={{
                    fontSize: '24px',
                    lineHeight: '32px',
                    fontWeight: 600,
                  }}
                >
                  {journal.publisher.slug === 'elsevier' &&
                  (journal.slug === 'transfer-your-manuscript' ||
                    journal.slug === 'journal-finder')
                    ? t(
                        'section1.prepare_to_submit_to',
                        'Prepare to submit with ',
                      )
                    : t(
                        'v2_new_content.check_language_technical_readiness',
                        'Check language and technical readiness before submitting to',
                      )}
                </MainHeading>
              </CustomFontHeaderContainer>
              <SubHeadingNew
                variant={
                  journal.publisher.slug === 'bmj' ? 'nonItalic' : 'default'
                }
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                  fontSize: journal.publisher.slug === 'aipp' ? '45px' : '24px',
                }}
                aria-label={`${journal.publisher.label} / ${journal.label}`}
                data-test-id="journal-heading"
              >
                {showFullJournalName
                  ? journal.label
                  : truncateLabel(journal.label, 40)}
                {showTooltip ? (
                  <Tooltip>
                    {' '}
                    {journal.publisher.label} / {journal.label}
                  </Tooltip>
                ) : (
                  <></>
                )}
              </SubHeadingNew>
              {journal.articleTypes?.length === 1 ? (
                <TextDefault>
                  <></>
                </TextDefault>
              ) : (
                <ChangeArticleConfig
                  journal={journal}
                  onOptionSelect={(selectedId: string) =>
                    setSelectedArticleTypeId(Number(selectedId))
                  }
                />
              )}
              <SubHeadingNew2>
                {journal.publisher.slug === 'paperpal-preflight'
                  ? t(
                      'section1_v1.assessment_v1_pp',
                      'Assessments configured by the journal editors:',
                    )
                  : t(
                      'v2_new_content.assessment_check_journal_requirements',
                      'Assessment check based on journal requirements',
                    )}
              </SubHeadingNew2>

              {loading ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <SkeletonLoader
                    height="27px"
                    width="35%"
                    background={theme.colors.gray5.value}
                  />
                  <SkeletonLoader
                    height="27px"
                    width="45%"
                    background={theme.colors.gray5.value}
                  />
                </div>
              ) : (
                <TextDefault2>
                  {techCheckCount > 0 && langCheckCount > 0 ? (
                    <>
                      <LangTechEvaluations>
                        <Image
                          width={16}
                          height={22}
                          src="/green-new-tick.svg"
                          alt="Green Tick"
                          style={{
                            display: 'inline',
                            marginRight: '8px',
                            verticalAlign: 'middle',
                          }}
                        />
                        <span
                          data-test-id="language-check-count"
                          style={{
                            fontWeight: 'bold',
                            paddingRight: '4px',
                            cursor: 'pointer',
                            color: showTooltip2
                              ? theme.colors.blue10.value
                              : 'inherit',
                          }}
                          onMouseEnter={() => handleMouseEnter2()}
                          onMouseLeave={() => handleMouseLeave2()}
                        >
                          {langCheckCount}
                        </span>
                        {mutateString(
                          t('section1_v1.is_manuscript_ready_description.lang'),
                          {},
                        )}
                      </LangTechEvaluations>
                      {showTooltip2 ? (
                        <TooltipNew>
                          <ExpandableTextUL>
                            {languageCheckLists.map((check, index) => (
                              <ExpandableTextLI key={index}>
                                <span>{check.label}</span>
                              </ExpandableTextLI>
                            ))}
                          </ExpandableTextUL>
                        </TooltipNew>
                      ) : (
                        <></>
                      )}

                      <LangTechEvaluations>
                        <Image
                          width={16}
                          height={22}
                          src="/green-new-tick.svg"
                          alt="Green Tick"
                          style={{
                            display: 'inline',
                            marginRight: '8px',
                            verticalAlign: 'middle',
                          }}
                        />
                        <span
                          data-test-id="technical-check-count"
                          style={{
                            fontWeight: 'bold',
                            paddingRight: '4px',
                            cursor: 'pointer',
                            color: showTooltip3
                              ? theme.colors.blue10.value
                              : 'inherit',
                          }}
                          onMouseEnter={() => handleMouseEnter3()}
                          onMouseLeave={() => handleMouseLeave3()}
                        >
                          {techCheckCount}
                        </span>
                        {mutateString(
                          t('section1_v1.is_manuscript_ready_description.tech'),
                          {},
                        )}
                      </LangTechEvaluations>
                      {showTooltip3 ? (
                        <TooltipNew2
                          ref={tooltipRef}
                          onMouseEnter={handleTooltipMouseEnter}
                          onMouseLeave={handleTooltipMouseLeave}
                        >
                          <ExpandableTextUL>
                            {technicalCheckLists.map((check, index) => (
                              <ExpandableTextLI key={index}>
                                <span>{check.label}</span>
                              </ExpandableTextLI>
                            ))}
                          </ExpandableTextUL>
                        </TooltipNew2>
                      ) : (
                        <></>
                      )}
                    </>
                  ) : techCheckCount > 0 ? (
                    <>
                      <LangTechEvaluations>
                        <Image
                          width={16}
                          height={22}
                          src="/green-new-tick.svg"
                          alt="Green Tick"
                          style={{
                            display: 'inline',
                            marginRight: '8px',
                            verticalAlign: 'middle',
                          }}
                        />
                        <span
                          data-test-id="technical-check-count"
                          style={{
                            fontWeight: 'bold',
                            paddingRight: '4px',
                            cursor: 'pointer',
                            color: showTooltip3
                              ? theme.colors.blue10.value
                              : 'inherit',
                          }}
                          onMouseEnter={() => handleMouseEnter3()}
                          onMouseLeave={() => handleMouseLeave3()}
                        >
                          {techCheckCount}
                        </span>
                        {mutateString(
                          t('section1_v1.is_manuscript_ready_description.tech'),
                          {},
                        )}
                      </LangTechEvaluations>
                      {showTooltip3 ? (
                        <TooltipNew2
                          ref={tooltipRef}
                          onMouseEnter={handleTooltipMouseEnter}
                          onMouseLeave={handleTooltipMouseLeave}
                        >
                          <ExpandableTextUL>
                            {technicalCheckLists.map((check, index) => (
                              <ExpandableTextLI key={index}>
                                <span>{check.label}</span>
                              </ExpandableTextLI>
                            ))}
                          </ExpandableTextUL>
                        </TooltipNew2>
                      ) : (
                        <></>
                      )}
                    </>
                  ) : langCheckCount > 0 ? (
                    <>
                      <LangTechEvaluations>
                        <Image
                          width={16}
                          height={22}
                          src="/green-new-tick.svg"
                          alt="Green Tick"
                          style={{
                            display: 'inline',
                            marginRight: '8px',
                            verticalAlign: 'middle',
                          }}
                        />
                        <span
                          data-test-id="language-check-count"
                          style={{
                            fontWeight: 'bold',
                            paddingRight: '4px',
                            cursor: 'pointer',
                            color: showTooltip2
                              ? theme.colors.blue10.value
                              : 'inherit',
                          }}
                          onMouseEnter={() => handleMouseEnter2()}
                          onMouseLeave={() => handleMouseLeave2()}
                        >
                          {langCheckCount}
                        </span>
                        {mutateString(
                          t('section1_v1.is_manuscript_ready_description.lang'),
                          {},
                        )}
                      </LangTechEvaluations>
                      {showTooltip2 ? (
                        <TooltipNew>
                          <ExpandableTextUL>
                            {languageCheckLists.map((check, index) => (
                              <ExpandableTextLI key={index}>
                                <span>{check.label}</span>
                              </ExpandableTextLI>
                            ))}
                          </ExpandableTextUL>
                        </TooltipNew>
                      ) : (
                        <></>
                      )}
                    </>
                  ) : null}
                </TextDefault2>
              )}
            </>
          )
        ) : (
          //Section 3 for publishers paperpal, gjd-journal-finder , editage-us
          <>
            <CustomFontHeaderContainer journal={mappedJournal}>
              <MainHeading
                style={{
                  fontSize: '24px',
                  lineHeight: '32px',
                  fontWeight: 600,
                }}
              >
                {journal.publisher.slug === 'gjd-journal-finder' ||
                journal.publisher.slug === 'editage-cn' ||
                journal.publisher.slug === 'paperpal'
                  ? t(
                      'section1.make_manuscript_ready_for_journal_submission',
                      'Get your manuscript checked against standard pre-submission checks',
                    )
                  : t(
                      'section1.is_manuscript_ready_for_journal_submission',
                      'Is your manuscript ready for journal submission?',
                    )}
              </MainHeading>
            </CustomFontHeaderContainer>
            {journal.publisher.slug !== 'gjd-journal-finder' &&
              journal.publisher.slug !== 'editage-cn' &&
              journal.publisher.slug !== 'paperpal' && (
                <SubHeadingNew
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  style={{
                    fontSize: '28px',
                  }}
                  aria-label={`${journal.publisher.label} / ${journal.label}`}
                  data-test-id="journal-heading"
                >
                  {truncateLabel(journal.publisher.label ?? '', 20)} /{' '}
                  {truncateLabel(journal.label, 20)}
                  {showTooltip ? (
                    <Tooltip>
                      {' '}
                      {journal.publisher.label} / {journal.label}
                    </Tooltip>
                  ) : (
                    <></>
                  )}
                </SubHeadingNew>
              )}
            <SubHeadingNew2>
              {t(
                'v2_new_content.assessment_check_journal_requirements_paperpal',
                'Your manuscript will be checked for:',
              )}
            </SubHeadingNew2>

            {loading ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                <SkeletonLoader
                  height="27px"
                  width="35%"
                  background={theme.colors.gray5.value}
                />
                <SkeletonLoader
                  height="27px"
                  width="45%"
                  background={theme.colors.gray5.value}
                />
              </div>
            ) : (
              <TextDefault2>
                {techCheckCount > 0 && langCheckCount > 0 ? (
                  <>
                    <LangTechEvaluations>
                      <Image
                        width={16}
                        height={22}
                        src="/green-new-tick.svg"
                        alt="Green Tick"
                        style={{
                          display: 'inline',
                          marginRight: '8px',
                          verticalAlign: 'middle',
                        }}
                      />
                      <span
                        data-test-id="language-check-count"
                        style={{
                          fontWeight: 'bold',
                          paddingRight: '4px',
                          cursor: 'pointer',
                          color: showTooltip2
                            ? theme.colors.blue10.value
                            : 'inherit',
                        }}
                        onMouseEnter={() => handleMouseEnter2()}
                        onMouseLeave={() => handleMouseLeave2()}
                      >
                        {langCheckCount}
                      </span>
                      {mutateString(
                        t('section1_v1.is_manuscript_ready_description.lang'),
                        {},
                      )}
                    </LangTechEvaluations>
                    {showTooltip2 ? (
                      <TooltipNew>
                        <ExpandableTextUL>
                          {languageCheckLists.map((check, index) => (
                            <ExpandableTextLI key={index}>
                              <span>{check.label}</span>
                            </ExpandableTextLI>
                          ))}
                        </ExpandableTextUL>
                      </TooltipNew>
                    ) : (
                      <></>
                    )}

                    <LangTechEvaluations>
                      <Image
                        width={16}
                        height={22}
                        src="/green-new-tick.svg"
                        alt="Green Tick"
                        style={{
                          display: 'inline',
                          marginRight: '8px',
                          verticalAlign: 'middle',
                        }}
                      />
                      <span
                        data-test-id="technical-check-count"
                        style={{
                          fontWeight: 'bold',
                          paddingRight: '4px',
                          cursor: 'pointer',
                          color: showTooltip3
                            ? theme.colors.blue10.value
                            : 'inherit',
                        }}
                        onMouseEnter={() => handleMouseEnter3()}
                        onMouseLeave={() => handleMouseLeave3()}
                      >
                        {techCheckCount}
                      </span>
                      {mutateString(
                        t('section1_v1.is_manuscript_ready_description.tech'),
                        {},
                      )}
                    </LangTechEvaluations>
                    {showTooltip3 ? (
                      <TooltipNew2
                        ref={tooltipRef}
                        onMouseEnter={handleTooltipMouseEnter}
                        onMouseLeave={handleTooltipMouseLeave}
                      >
                        <ExpandableTextUL>
                          {technicalCheckLists.map((check, index) => (
                            <ExpandableTextLI key={index}>
                              <span>{check.label}</span>
                            </ExpandableTextLI>
                          ))}
                        </ExpandableTextUL>
                      </TooltipNew2>
                    ) : (
                      <></>
                    )}
                  </>
                ) : techCheckCount > 0 ? (
                  <>
                    <LangTechEvaluations>
                      <Image
                        width={16}
                        height={22}
                        src="/green-new-tick.svg"
                        alt="Green Tick"
                        style={{
                          display: 'inline',
                          marginRight: '8px',
                          verticalAlign: 'middle',
                        }}
                      />
                      <span
                        data-test-id="technical-check-count"
                        style={{
                          fontWeight: 'bold',
                          paddingRight: '4px',
                          cursor: 'pointer',
                          color: showTooltip3
                            ? theme.colors.blue10.value
                            : 'inherit',
                        }}
                        onMouseEnter={() => handleMouseEnter3()}
                        onMouseLeave={() => handleMouseLeave3()}
                      >
                        {techCheckCount}
                      </span>
                      {mutateString(
                        t('section1_v1.is_manuscript_ready_description.tech'),
                        {},
                      )}
                    </LangTechEvaluations>
                    {showTooltip3 ? (
                      <TooltipNew2
                        ref={tooltipRef}
                        onMouseEnter={handleTooltipMouseEnter}
                        onMouseLeave={handleTooltipMouseLeave}
                      >
                        <ExpandableTextUL>
                          {technicalCheckLists.map((check, index) => (
                            <ExpandableTextLI key={index}>
                              <span>{check.label}</span>
                            </ExpandableTextLI>
                          ))}
                        </ExpandableTextUL>
                      </TooltipNew2>
                    ) : (
                      <></>
                    )}
                  </>
                ) : langCheckCount > 0 ? (
                  <>
                    <LangTechEvaluations>
                      <Image
                        width={16}
                        height={22}
                        src="/green-new-tick.svg"
                        alt="Green Tick"
                        style={{
                          display: 'inline',
                          marginRight: '8px',
                          verticalAlign: 'middle',
                        }}
                      />
                      <span
                        data-test-id="language-check-count"
                        style={{
                          fontWeight: 'bold',
                          paddingRight: '4px',
                          cursor: 'pointer',
                          color: showTooltip2
                            ? theme.colors.blue10.value
                            : 'inherit',
                        }}
                        onMouseEnter={() => handleMouseEnter2()}
                        onMouseLeave={() => handleMouseLeave2()}
                      >
                        {langCheckCount}
                      </span>
                      {mutateString(
                        t('section1_v1.is_manuscript_ready_description.lang'),
                        {},
                      )}
                    </LangTechEvaluations>
                    {showTooltip2 ? (
                      <TooltipNew>
                        <ExpandableTextUL>
                          {languageCheckLists.map((check, index) => (
                            <ExpandableTextLI key={index}>
                              <span>{check.label}</span>
                            </ExpandableTextLI>
                          ))}
                        </ExpandableTextUL>
                      </TooltipNew>
                    ) : (
                      <></>
                    )}
                  </>
                ) : null}
              </TextDefault2>
            )}
            {journal.articleTypes?.length === 1 ? (
              <TextDefault>
                <></>
              </TextDefault>
            ) : (
              <ChangeArticleConfig
                journal={journal}
                onOptionSelect={(selectedId: string) =>
                  setSelectedArticleTypeId(Number(selectedId))
                }
              />
            )}
          </>
        )}

        <ButtonGroup
          style={{
            marginTop: '28px',
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
          }}
        >
          <ButtonWrap>
            <UploadButtonNew
              id="Run-Pre-Submission Checks-Free"
              disabled={
                !showQuickView &&
                (uploadState.name === 'loading' ||
                  uploadState.name === 'success')
              }
              onClick={() => {
                trackEvent([
                  'Manuscript upload intent',
                  'Upload click',
                  'Run pre-submission checks Free',
                  {
                    Section: 'Section 1',
                  },
                ]);
                setOtherSectionClicked(false);
                if (errorSection !== 'Section_1' && errorSection !== '') {
                  setOtherSectionClicked(true);
                  setErrorScreen(false);
                }
                setErrorSection('Section_1');
              }}
              onFileSelected={onFileSelected}
              size="small"
              text={t('section1_v2.upload_for_free_assessment')}
              isIcon={true}
              isHoverPrimary={true}
              style={{
                fontSize: '20px',
                width: '360px',
                boxShadow: 'none',
              }}
              optionalLanguageText={{
                processing: t('common.processing'),
              }}
              trackingProperties={{
                CTALabel: 'Upload for free assessment',
                elementName: 'manuscript_upload',
                sectionName: 'section_1',
              }}
              isAIPP={journal.publisher.slug === 'aipp' ? true : false}
            />
          </ButtonWrap>
          {/* {(journal.publisher.slug === 'gjd-journal-finder' ||
            journal.publisher.slug === 'editage-cn') && (
            <TrustedByContainer>
              <VerticalSeparator />
              <TrustedByText>
                <TrustedByLabel>❤️ Trusted by</TrustedByLabel>
                <ResearchersCount>1.5Mn+ Researchers</ResearchersCount>
              </TrustedByText>
            </TrustedByContainer>
          )} */}
          {submitPage ? (
            <>
              {journal.url ? (
                <SubmissionBtnWrap style={{ minWidth: '215px' }}>
                  {journal.publisher.slug === 'aipp' ? (
                    <ButtonNew
                      size="small"
                      as="a"
                      href={journal.url}
                      onClick={() => {
                        trackEvent([
                          'Manuscript upload intent',
                          'Secondary click',
                          'Skip to submission',
                          {
                            Section: 'Section 1',
                          },
                        ]);
                      }}
                      trackingProperties={{
                        CTALabel: 'Skip to submission',
                        elementName: 'manuscript_upload',
                        sectionName: 'section_1',
                      }}
                      style={{
                        fontSize: '16px',
                      }}
                    >
                      <span>
                        {t(
                          'section1_v1.skip_submission',
                          'Skip to submission >',
                        )}
                      </span>
                    </ButtonNew>
                  ) : (
                    <Button
                      size="small"
                      as="a"
                      href={journal.url}
                      onClick={() => {
                        trackEvent([
                          'Manuscript upload intent',
                          'Secondary click',
                          'Skip to submission',
                          {
                            Section: 'Section 1',
                          },
                        ]);
                      }}
                      style={{
                        fontSize: '16px',
                        color: [
                          'hepatol',
                          'livertransp',
                          'clinliverdis',
                          'hepatolcom',
                        ].includes(journal.slug)
                          ? 'yellow'
                          : 'gray1',
                      }}
                      css={{
                        '&:hover': {
                          backgroundColor: theme.colors.gray3,
                        },
                      }}
                    >
                      <span>
                        {t(
                          'section1_v1.skip_submission',
                          'Skip to submission >',
                        )}
                      </span>
                    </Button>
                  )}
                </SubmissionBtnWrap>
              ) : (
                <SubmissionBtnWrap>
                  <Button
                    disabled
                    size="small"
                    title="Contact the journal editor for submission URL"
                    style={{ fontSize: '16px', width: 'auto' }}
                  >
                    {t('section1_v1.skip_submission', 'Skip to submission >')}
                  </Button>
                </SubmissionBtnWrap>
              )}
            </>
          ) : null}
        </ButtonGroup>

        <FormatAccepted
          aria-label="Formats accepted"
          data-test-id="formats-accepted"
          style={{
            marginBottom:
              journal.publisher.slug === 'gjd-journal-finder' ||
              journal.publisher.slug === 'editage-cn'
                ? '-90px'
                : '',
          }}
        >
          {t('section1_v1.formats_accepted', 'Formats accepted')}
          <Image
            width={36}
            height={36}
            src="/pdf-word-group.svg"
            alt={journal.label}
            style={{
              display: 'inline',
              marginLeft: '5px',
              marginRight: '5px',
            }}
          />
          {t('section1_v1.size_accepted', 'Size accepted')}
        </FormatAccepted>
      </MainSection>
    </>
  );
};

const MainSection = styled('div', {
  flex: 1,
});

const ButtonWrap = withOnClickTracking(
  styled('div', {
    minWidth: '300px',
    '& > Button': {
      minWidth: '300px',
      height: '60px',
    },
    '& > Button:hover': {
      backgroundColor: theme.colors.blue11,
    },
  }),
);

export const SectionTitle = styled('span', {
  display: 'flex',
  fontSize: '20px',
  color: theme.colors.blue11,
  fontWeight: 700,
  justifyContent: 'center',
  justifyItems: 'center',
  textTransform: 'uppercase',
});

export const SectionNewV1 = styled('div', {
  paddingTop: rem(48),
  paddingBottom: rem(80),
  background: theme.colors.white,
  paddingLeft: rem(32),
  paddingRight: rem(32),
  minWidth: rem(1234),
  variants: {
    size: {
      sm: {
        paddingTop: rem(40),
        paddingBottom: rem(40),
      },
      md: {
        paddingTop: rem(60),
        paddingBottom: rem(60),
      },
      lg: {
        paddingTop: rem(70),
        paddingBottom: rem(70),
      },
    },
    bgTheme: {
      gray: {
        background: theme.colors.gray2,
      },
      grayDark: {
        background: theme.colors.gray5,
      },
      yellow: {
        background:
          'linear-gradient(to top, rgba(249, 240, 209, 1), rgba(249, 240, 209, 0.5), rgba(249, 240, 209, 0))',
      },
      darkBlue: {
        background: 'rgba(5, 30, 87, 1)',
      },
      lightBlue1: {
        background: 'rgba(237, 243, 255, 1)',
      },
      lightBlue2: {
        background: 'rgba(225, 235, 255, 1)',
      },
      white: {
        background: 'rgba(255, 255, 255, 1)',
      },
    },
  },
});

export const MainHeading = styled('span', {
  fontSize: '28px',
  lineHeight: '40px',
  fontWeight: '700',
  display: 'block',
  color: 'rgba(5, 30, 87, 1)',
  // textTransform: 'capitalize',
});

const Tooltip = styled('div', {
  position: 'absolute',
  backgroundColor: theme.colors.blue12,
  color: theme.colors.white,
  padding: '5px 10px',
  borderRadius: '4px',
  fontSize: '14px',
  top: '100%',
  left: '40%',
  lineHeight: '22px',
  fontWeight: '400',
  fontStyle: 'normal',
});

const TextDefault2 = styled('div', {
  fontSize: '14px',
  lineHeight: '24px',
  fontWeight: '400',
  color: 'rgba(5, 30, 87, 1)',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const SubHeadingNew = styled('div', {
  fontSize: '24px',
  lineHeight: '1.4',
  fontWeight: '700',
  marginTop: '10px',
  fontFamily: 'IBM Plex Serif',
  color: 'rgba(10, 135, 52, 1)',

  position: 'relative',
  variants: {
    variant: {
      nonItalic: {
        fontStyle: 'normal',
      },
      default: {
        fontStyle: 'normal',
      },
    },
  },
});
const SubHeadingNew2 = styled('div', {
  fontSize: '14px',
  lineHeight: '24px',
  fontWeight: '600',
  color: '#051E57',
  marginTop: '20px',
  marginBottom: '10px',
});
const FormatAccepted = styled('div', {
  fontSize: '12px',
  lineHeight: '12px',
  fontWeight: '500',
  marginTop: '10px',
  color: theme.colors.gray11,
  display: 'flex',
  alignItems: 'center',
  marginBottom: '80px',
});
const LangTechEvaluations = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

const TooltipNew = styled('div', {
  position: 'absolute',
  backgroundColor: theme.colors.blue12,
  color: theme.colors.white,
  padding: '5px 10px',
  borderRadius: '4px',
  fontSize: '14px',
  height: 'max-content',
  fontFamily: 'IBM Plex Sans',
  textAlign: 'left',
  width: '168px',
  lineHeight: '22px',
  fontWeight: '400',
  fontStyle: 'normal',
  transform: 'translateY(10%)',
  marginTop: '8px',
});
const TooltipNew2 = styled('div', {
  position: 'absolute',
  transform: 'translateY(30%)',
  marginTop: '2px',
  backgroundColor: theme.colors.blue12,
  color: theme.colors.white,
  padding: '5px 10px',
  borderRadius: '4px',
  fontSize: '14px',
  height: 'max-content',
  fontFamily: 'IBM Plex Sans',
  textAlign: 'left',
  width: '368px',
  lineHeight: '22px',
  fontWeight: '400',
  fontStyle: 'normal',
  maxHeight: '200px',
  overflowY: 'auto',
  wordWrap: 'break-word',
  whiteSpace: 'normal',
  zIndex: 9999,
});
const ExpandableTextUL = styled('ul', {
  margin: 0,
  padding: '0',
});

const ExpandableTextLI = styled('li', {
  display: 'flex',

  fontSize: '12px',
  fontWeight: 400,
  color: theme.colors.white,
  margin: '10px 0',

  counterIncrement: 'list-item',
  '&::before': {
    content: 'counter(list-item) ". "',
    fontWeight: 'bold',
    marginRight: '10px',
  },
});

const TextDefault = styled('div', {
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: '600',
  color: 'rgba(5, 30, 87, 1)',
  marginTop: '25px',
});
function truncateLabel(title: string, length: number) {
  if (title.length > length) {
    const text = title.substring(0, length).trim();
    return `${text}...`;
  } else {
    return title;
  }
}
const SubmissionBtnWrap = withOnClickTracking(
  styled('div', {
    '& > button': {
      height: '60px',
      width: 220,
    },
    '& > a': {
      height: '60px',
      display: 'inline-flex',
      alignItems: 'center',
    },
  }),
);

// const TrustedByContainer = styled('div', {
//   display: 'flex',
//   alignItems: 'center',
//   marginLeft: '20px',
// });

// const VerticalSeparator = styled('div', {
//   width: '1px',
//   height: '50px',
//   backgroundColor: theme.colors.gray9.value,
//   marginRight: '20px',
// });

// const TrustedByText = styled('div', {
//   display: 'flex',
//   flexDirection: 'column',
//   alignItems: 'flex-start',
// });

// const TrustedByLabel = styled('div', {
//   fontFamily: 'IBM Plex Sans',
//   fontSize: '13.99px',
//   lineHeight: '16.47px',
//   letterSpacing: '0%',
//   verticalAlign: 'middle',
//   color: 'rgba(93, 106, 129, 1)',
//   fontWeight: '400',
// });

// const ResearchersCount = styled('div', {
//   fontFamily: 'IBM Plex Sans',
//   fontSize: '13.99px',
//   lineHeight: '16.47px',
//   letterSpacing: '0%',
//   verticalAlign: 'middle',
//   color: 'rgba(5, 30, 87, 1)',
//   fontWeight: '600',
// });
